import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  MemphisOperatorListFilters,
  MemphisOperatorRepository,
} from '@app/domain/ports/tpv/memphis-operator.repository';
import { TpvMemphisOperator } from '@app/domain/models/tpv/memphis-operator.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { TpvReportFile, TpvReportFormat } from '@app/domain/models/tpv/tpv-report.model';
import { MemphisOperatorDto } from '@app/data/dtos/tpv/memphis-operator.dto';
import { TpvReportFileDto } from '@app/data/dtos/tpv/tpv-report.dto';
import { mapMemphisOperator } from '@app/data/mappers/tpv/memphis-operator.mapper';
import { toTpvPage } from '@app/data/mappers/tpv/tpv-page.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/** `GET /api/v1/tpv/operadores/` (+ `/report/`). Auth vía interceptores estándar. */
@Injectable()
export class MemphisOperatorRemoteDataSource extends MemphisOperatorRepository {
  private readonly http = inject(HttpClient);

  override listOperators(
    filters: MemphisOperatorListFilters = {},
  ): Observable<TpvPage<TpvMemphisOperator>> {
    const page = filters.page ?? 1;
    const limit = filters.limit ?? 10;
    const params = toHttpParams({
      search: filters.search,
      name: filters.name,
      email: filters.email,
      active: typeof filters.active === 'boolean' ? filters.active : undefined,
      id_user_memphis: filters.idUserMemphis,
      page,
      limit,
    });

    return this.http
      .get<TpvEnvelope<unknown>>(tpvUrl('operadores/'), { params })
      .pipe(map((raw) => toTpvPage<MemphisOperatorDto, TpvMemphisOperator>(raw, mapMemphisOperator, { page, limit })));
  }

  override getOperatorsReport(
    format: TpvReportFormat,
    filters: Omit<MemphisOperatorListFilters, 'page' | 'limit'>,
  ): Observable<TpvReportFile> {
    const params = toHttpParams({
      format,
      search: filters.search,
      name: filters.name,
      email: filters.email,
      active: typeof filters.active === 'boolean' ? filters.active : undefined,
      id_user_memphis: filters.idUserMemphis,
    });

    return this.http
      .get<TpvEnvelope<TpvReportFileDto>>(tpvUrl('operadores/report/'), { params })
      .pipe(
        map((raw) => {
          const dto = unwrapTpv<TpvReportFileDto>(raw);
          return {
            fileName: dto?.file_name?.trim() || 'operadores',
            fileBase64: dto?.file_base64 ?? '',
            fileSize: dto?.file_size ?? 0,
          };
        }),
      );
  }
}
